/**
 * Raccourci d'ouverture et sa notation.
 *
 * `⌘K` ouvre la palette partout ; `Ctrl+K` aussi, **sauf sous Apple** ou il vaut
 * « supprimer jusqu'a la fin de la ligne » dans un champ de saisie et ne doit pas
 * etre detourne. La **notation affichee** suit la plateforme.
 */

interface NavigatorWithPlatform {
  readonly platform?: string;
  readonly userAgentData?: { readonly platform?: string };
}

/** Plateformes d'Apple, ou la touche de commande s'ecrit « ⌘ ». */
export const isApplePlatform = (): boolean => {
  const nav: NavigatorWithPlatform = globalThis.navigator;

  return /mac|iphone|ipad|ipod/iu.test(nav.userAgentData?.platform ?? nav.platform ?? '');
};

/** Touche de modification telle qu'elle est gravee : « ⌘ » ou « Ctrl ». */
export const modifierLabel = (): string => (isApplePlatform() ? '⌘' : 'Ctrl');

/** `⌘K` ou `Ctrl K`, pour le champ de la barre superieure. */
export const shortcutLabel = (): string => (isApplePlatform() ? '⌘K' : 'Ctrl K');

/** Valeur d'`aria-keyshortcuts`, selon la plateforme. */
export const shortcutAria = (): string => (isApplePlatform() ? 'Meta+K' : 'Control+K Meta+K');

/**
 * Une zone de saisie **multiligne** garde ses propres raccourcis : la palette
 * n'y est pas ouverte et le navigateur n'est pas contrarie. Une saisie sur une
 * ligne, elle, laisse passer le raccourci (c'est le cas de la palette elle-meme).
 */
export const isMultilineField = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.tagName === 'TEXTAREA') {
    return true;
  }

  const editable = target.getAttribute('contenteditable');

  return target.isContentEditable || (editable !== null && editable !== 'false');
};
